/**
 * Frontend error reporting (M-BIG3 / err-15 / M-ALERT1).
 *
 * The single funnel: the React error boundary, window.onerror, and
 * unhandledrejection all land here.
 *
 * ⚠️ The browser does NOT talk to the error store. GlitchTip is private (no
 * public endpoint), so reports are POSTed to our own API at /client-errors,
 * which scrubs them SERVER-SIDE and relays them over the private network.
 * That means PII scrubbing is enforced somewhere we control and test, not in
 * browser code a future change could bypass.
 *
 * We send only: the error message, the stack, and the route PATTERN. Never the
 * student id, the roster, answers, or the raw URL (which contains ids) — these
 * run on shared classroom devices.
 */

// Strip identifiers out of a URL before it ever leaves the browser. The server
// scrubs again; this is belt and braces.
const scrubUrl = (raw: string): string =>
	raw
		.replace(/\/students\/[^/?#]+/gi, '/students/[id]')
		.replace(/\/classrooms\/[^/?#]+/gi, '/classrooms/[id]')
		.replace(/\/classes\/[^/?#]+/gi, '/classes/[id]')
		.replace(/\/lessons\/[^/?#]+/gi, '/lessons/[id]')
		.replace(/\/questions\/[^/?#]+/gi, '/questions/[id]')
		.replace(/\/live\/[^/?#]+/gi, '/live/[code]')
		.replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '[email]')
		.split('?')[0]   // query strings can carry ids

const API_BASE = window.location.hostname === 'localhost'
	? 'http://api.localhost'
	: (window.location.hostname.includes('staging.')
		? 'https://api.staging.allforonelearn.com'
		: 'https://api.allforonelearn.com')

/** Relay to our own API (which scrubs + forwards to the private GlitchTip). */
const sendToApi = (source: string, error: Error, context?: Record<string, string>) => {
	try {
		const payload = JSON.stringify({
			source,
			message: String(error.message ?? '').slice(0, 500),
			stack: String(error.stack ?? '').slice(0, 8000),
			url: scrubUrl(window.location.pathname),
			boundary: context?.boundary,
		})
		// keepalive so a report survives the page unloading after a crash
		fetch(`${API_BASE}/client-errors`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: payload,
			keepalive: true,
			credentials: 'omit',   // no cookies — nothing to identify the student
		}).catch(() => {})
	} catch {
		// reporting must never break the page
	}
}

type ErrorSource = 'react-boundary' | 'window.onerror' | 'unhandledrejection' | 'manual'

export const reportError = (source: ErrorSource, error: unknown, context?: Record<string, string>) => {
	const err = error instanceof Error ? error : new Error(String(error))
	// eslint-disable-next-line no-console
	console.error(`[afo-error] source=${source}`, err.message, context ?? '', err.stack ?? '')
	sendToApi(source, err, context)
	// Optional extra transport (kept for future use / tests)
	transport?.(source, err, context)
}

type Transport = (source: ErrorSource, error: Error, context?: Record<string, string>) => void
let transport: Transport | null = null
export const installErrorTransport = (t: Transport) => { transport = t }

/** Install once at app start: makes every uncaught failure visible. */
export const installGlobalErrorHandlers = () => {
	window.addEventListener('error', event => {
		reportError('window.onerror', event.error ?? event.message, {
			file: String(event.filename ?? ''),
			line: String(event.lineno ?? ''),
		})
	})
	window.addEventListener('unhandledrejection', event => {
		reportError('unhandledrejection', event.reason)
	})
}
